import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import API from "../../../utils/helpers/APIHelper";
import { RootState } from "../../store";
import ErrorHandler from "../../../utils/helpers/ErrorHandler";

interface GoogleReview {
  author_name: string;
  author_url: string;
  profile_photo_url: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
}

interface GoogleReviewsState {
  loading: boolean;
  reviews: GoogleReview[];
  averageRating: number;
  totalReviews: number;
  error: string | null;
}

const initialState: GoogleReviewsState = {
  loading: false,
  reviews: [],
  averageRating: 0,
  totalReviews: 0,
  error: null,
};

// Async thunk to fetch Google reviews
export const fetchGoogleReviews = createAsyncThunk<
  any,
  string, // placeId
  { state: RootState; rejectValue: any }
>("googleReviews/fetchReviews", async (placeId, { rejectWithValue }) => {
  try {
    const response = await API.get(`/api/v1/google-reviews?placeId=${placeId}`);
    return response;
  } catch (error) {
    ErrorHandler(error);
    return rejectWithValue(error);
  }
});

const googleReviewsSlice = createSlice({
  name: "googleReviews",
  initialState,
  reducers: {
    clearReviews: (state) => {
      state.reviews = [];
      state.averageRating = 0;
      state.totalReviews = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGoogleReviews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchGoogleReviews.fulfilled,
        (state, action: PayloadAction<any>) => {
          const data = action.payload;
          if (data.result) {
            state.reviews = data.result.reviews || [];
            state.averageRating = data.result.rating || 0;
            state.totalReviews = data.result.user_ratings_total || 0;
          }
          state.loading = false;
          state.error = null;
        }
      )
      .addCase(fetchGoogleReviews.rejected, (state, action) => {
        state.loading = false;
        state.error = "Unable to load reviews at this time";
        state.reviews = [];
        state.averageRating = 0;
        state.totalReviews = 0;
      });
  },
});

export const { clearReviews } = googleReviewsSlice.actions;
export default googleReviewsSlice.reducer;